import { getCryptoCollectionPageGroupBySlug } from '@/api/market';
import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import {
  parseMinuteUpdownSlug,
  resolveParsedUpdownRowWindow,
  resolveUpdownFeedGroupKey,
  resolveUpdownRoundSlugSource,
  updownRoundFeedDistance,
} from '@/domain/crypto/crypto-minute-slug.js';

/**
 * cryptoPageGroupBySlug 响应归一：OpenAPI 为 CollectionVo[]，兼容 TableDataInfo.rows。
 * @param {unknown} data
 * @returns {object[]}
 */
export function normalizeGroupBySlugResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

/**
 * @param {object} [query] CryptoCollectionQuery
 * @returns {Promise<object[]>}
 */
export async function fetchCryptoGroupBySlugRows(query = {}) {
  const data = await getCryptoCollectionPageGroupBySlug(query ?? {});
  return normalizeGroupBySlugResponse(data);
}

/**
 * Group 首屏 → paginated-feed 契约。
 * hasMore=false：等静默 page 回填后再开放 loadMore，避免与回填竞态。
 * @param {object[]} rows
 * @param {(rows: object[]) => object[]} [normalizeRows]
 */
export function toGroupBootstrapPageResult(rows, normalizeRows) {
  const list = typeof normalizeRows === 'function' ? normalizeRows(rows) : rows;
  return {
    apiRows: Array.isArray(list) ? list : [],
    nextPage: 0,
    hasMore: false,
    replaceRows: true,
  };
}

/**
 * Feed 行去重键：分钟盘用 seriesKey（btc-updown-5m），其它用 collection id。
 * @param {object} row
 * @returns {string}
 */
export function resolveFeedRowMergeKey(row) {
  const slug = resolveUpdownRoundSlugSource(row) || resolveCollectionSlug(row);
  const parsed = parseMinuteUpdownSlug(slug);
  const groupKey = resolveUpdownFeedGroupKey(parsed);
  if (groupKey) return `series:${groupKey}`;
  const id = String(row?.id ?? row?.collectionId ?? '').trim();
  if (id) return `id:${id}`;
  const fallbackSlug = String(slug ?? '').trim();
  return fallbackSlug ? `slug:${fallbackSlug}` : '';
}

function resolveRowFeedDistance(row, nowSec) {
  const slug = resolveUpdownRoundSlugSource(row) || resolveCollectionSlug(row);
  const parsed = parseMinuteUpdownSlug(slug);
  if (!parsed?.roundTs) return Number.POSITIVE_INFINITY;
  const window = resolveParsedUpdownRowWindow(row, parsed);
  return updownRoundFeedDistance(window.roundTs, nowSec, window.endSec - window.roundTs);
}

/**
 * Group 保序在前；同 series 冲突时保留更接近 live 的一档（避免 Group 未来档盖住 page live）。
 * @param {object[]} groupRows
 * @param {object[]} pageRows
 * @param {number} [nowSec]
 * @returns {object[]}
 */
export function mergeGroupBootstrapWithPageRows(
  groupRows,
  pageRows,
  nowSec = Math.floor(Date.now() / 1000)
) {
  const group = Array.isArray(groupRows) ? groupRows : [];
  const page = Array.isArray(pageRows) ? pageRows : [];
  if (!page.length) return group;
  if (!group.length) return page;

  const now =
    Number.isFinite(Number(nowSec)) && Number(nowSec) > 0
      ? Math.floor(Number(nowSec))
      : Math.floor(Date.now() / 1000);

  /** @type {Map<string, number>} */
  const indexByKey = new Map();
  const out = [];

  for (const row of group) {
    const key = resolveFeedRowMergeKey(row);
    if (key) indexByKey.set(key, out.length);
    out.push(row);
  }

  for (const row of page) {
    const key = resolveFeedRowMergeKey(row);
    if (!key) {
      out.push(row);
      continue;
    }
    if (!indexByKey.has(key)) {
      indexByKey.set(key, out.length);
      out.push(row);
      continue;
    }

    // 非分钟盘 series：保留 Group 首屏
    if (!key.startsWith('series:')) continue;

    const idx = indexByKey.get(key);
    const existing = out[idx];
    if (resolveRowFeedDistance(row, now) < resolveRowFeedDistance(existing, now)) {
      out[idx] = row;
    }
  }

  return out;
}

/**
 * 静默 page 回填：拼在 Group 后面；page 空则保留 Group 首屏。
 * @param {{ state: { rawRows: object[] }, apiRows: object[], hadRows: boolean }} ctx
 */
export function resolveGroupBackfillRefreshRows({ state, apiRows, hadRows }) {
  if (!apiRows.length) {
    return hadRows ? state.rawRows : apiRows;
  }
  if (!hadRows) return apiRows;
  return mergeGroupBootstrapWithPageRows(state.rawRows, apiRows);
}

/**
 * 首屏 Group 快渲染，再静默跑 page 归集回填。
 * @param {object} store
 * @param {object} params
 * @param {boolean} params.append
 * @param {object} [params.options]
 * @param {(store: object, append: boolean, opts: object) => Promise<void>} params.runFetchPage
 * @param {object} params.groupFetchOptions
 * @param {object} params.pageFetchOptions
 * @param {() => boolean} [params.shouldBootstrapWithGroup]
 * @param {() => void} [params.afterGroupBootstrap]
 */
export async function runFeedGroupThenSilentPage(
  store,
  {
    append,
    options = {},
    runFetchPage,
    groupFetchOptions,
    pageFetchOptions,
    shouldBootstrapWithGroup,
    afterGroupBootstrap,
  }
) {
  const useGroup =
    !append &&
    !options.silent &&
    !options.pageOnly &&
    (typeof shouldBootstrapWithGroup !== 'function' || shouldBootstrapWithGroup());

  if (!useGroup) {
    if (append && store.silentBackfillPending) return;
    await runFetchPage(store, append, { ...pageFetchOptions, ...options });
    return;
  }

  store.silentBackfillPending = true;
  await runFetchPage(store, false, { ...groupFetchOptions, ...options });

  if (store.listFetchError && !store.rawRows.length) {
    store.silentBackfillPending = false;
    await runFetchPage(store, false, { ...pageFetchOptions, ...options, pageOnly: true });
    if (typeof afterGroupBootstrap === 'function') afterGroupBootstrap();
    return;
  }

  if (typeof afterGroupBootstrap === 'function') afterGroupBootstrap();

  void (async () => {
    try {
      await runFetchPage(store, false, {
        ...pageFetchOptions,
        silent: true,
        pageOnly: true,
        resolveRefreshRows: resolveGroupBackfillRefreshRows,
      });
      if (typeof afterGroupBootstrap === 'function') afterGroupBootstrap();
    } finally {
      store.silentBackfillPending = false;
    }
  })();
}
